import React from "react";
import Drawer from "@mui/material/Drawer";
import Switch from "@mui/material/Switch";
import VisibilityIcon from "@mui/icons-material/Visibility";

import DrawerHeader from "../../../shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../../shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../../../shared/helpers/ButtonOutlined";
import LoadSpinner from "../../../../../../utilities/LoadSpinner";

import ConditionalFieldWrapper from "./ConditionalFieldWrapper";
import ActionItemsSection from "./ActionItemsSection";
import CarryOverPanel from "./CarryOverPanel";

import { getRightworkAPI, postRightworkAPI } from "../../../utilities/requests";
import {
  API_create_check_in,
  API_get_check_in,
  API_update_check_in,
  API_update_check_in_responses,
  API_delete_check_in,
  API_create_check_in_action_item,
  API_update_check_in_action_item,
  API_toggle_check_in_action_item,
  API_delete_check_in_action_item,
} from "../../../../../../constants";


const AUTOSAVE_DEBOUNCE_MS = 1500;


function buildResponsesMap(checkIn) {
  if (!checkIn) return {};
  const map = {};
  (checkIn.sections || []).forEach((section) => {
    (section.fields || []).forEach((field) => {
      map[field.uuid] = field.response?.value ?? null;
    });
  });
  return map;
}


// A response value is one of the per-field-type shapes the renderers emit:
// { text }, { number }, { bool }, { option } — or null when empty/deselected.
// `false` for a yes/no field is a genuine answer, so this is intentionally not
// a plain truthiness check.
function valueHasContent(value) {
  if (value == null || typeof value !== "object") return false;
  if (typeof value.text === "string" && value.text.trim().length > 0) return true;
  if (typeof value.number === "number") return true;
  if (typeof value.bool === "boolean") return true;
  if (typeof value.option === "string" && value.option.length > 0) return true;
  return false;
}


// "Content recorded" = any answered field or any action item. Used to decide
// whether an exited draft is worth keeping. The share toggle and carry-over
// items act on other records, so they don't count.
function checkInHasContent(checkIn, responsesByFieldUuid) {
  if (!checkIn) return false;
  if ((checkIn.action_items || []).length > 0) return true;
  return Object.values(responsesByFieldUuid || {}).some(valueHasContent);
}


function CheckInForm(props) {
  const { open, onClose, mode, checkInUuid, employeeStmId, onSaved } = props;

  const [checkIn, setCheckIn] = React.useState(null);
  const [previousOpenActionItems, setPreviousOpenActionItems] = React.useState([]);
  const [responsesByFieldUuid, setResponsesByFieldUuid] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [actionError, setActionError] = React.useState(null);
  const [saveStatus, setSaveStatus] = React.useState("idle"); // idle | saving | saved | error

  // Set of field UUIDs whose response has changed since the last successful PATCH.
  const dirtyFieldUuidsRef = React.useRef(new Set());
  // Snapshot of the value to send, captured at debounce-fire time.
  const responsesByFieldUuidRef = React.useRef({});
  responsesByFieldUuidRef.current = responsesByFieldUuid;

  // Guard against stale fetch responses when the drawer re-opens for a
  // different check-in.
  const latestLoadIdRef = React.useRef(0);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadId = ++latestLoadIdRef.current;
    setIsLoading(true);
    setLoadError(null);
    setActionError(null);
    setSaveStatus("idle");
    dirtyFieldUuidsRef.current = new Set();
    // Defensive: cancel any pending debounced flush from a previous check-in
    // so it can't fire against the new one. With dirty set already cleared,
    // a stale flush would no-op anyway, but explicit beats implicit.
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }

    const finishLoad = (payload, errorMsg) => {
      if (cancelled || loadId !== latestLoadIdRef.current) return;
      if (errorMsg) {
        setLoadError(
          typeof errorMsg === "string"
            ? errorMsg
            : "Couldn't load check-in. Please try again.",
        );
      } else {
        setCheckIn(payload?.check_in || null);
        setPreviousOpenActionItems(payload?.previous_open_action_items || []);
        setResponsesByFieldUuid(buildResponsesMap(payload?.check_in));
      }
      setIsLoading(false);
    };

    (async () => {
      try {
        if (mode === "create") {
          const res = await postRightworkAPI(API_create_check_in, {
            store_team_member_id: employeeStmId,
          });
          if (res.status === 200) {
            finishLoad(res.data?.redux?.payload, null);
          } else {
            finishLoad(null, true);
          }
        } else {
          const res = await getRightworkAPI(API_get_check_in, {
            store_team_member_id: employeeStmId,
            check_in_uuid: checkInUuid,
          });
          if (res.status === 200) {
            finishLoad(res.data?.redux?.payload, null);
          } else {
            finishLoad(null, true);
          }
        }
      } catch (e) {
        // Surface the backend's message when available — e.g. "No check-in
        // template configured. Have your admin create one in Settings." —
        // so a new admin gets the onboarding nudge.
        const backendMsg = e?.response?.data?.system?.message;
        finishLoad(null, backendMsg || true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, mode, checkInUuid, employeeStmId]);

  // ----- Autosave debounce -----

  // Returns true if the flush succeeded (or was a no-op), false if the API
  // call failed. Callers that need to confirm persistence before proceeding
  // (e.g. the explicit Save button — see handleSave) should await + branch
  // on the return; fire-and-forget callers (autosave debounce, unmount
  // best-effort) can ignore it.
  const flushResponses = React.useCallback(async () => {
    const dirty = Array.from(dirtyFieldUuidsRef.current);
    if (dirty.length === 0 || !checkIn) return true;
    const responses = dirty.map((fieldUuid) => ({
      field_uuid: fieldUuid,
      value: responsesByFieldUuidRef.current[fieldUuid] ?? null,
    }));
    // Optimistically clear dirty set; if save fails, re-mark.
    const flushed = new Set(dirty);
    dirtyFieldUuidsRef.current = new Set();
    setSaveStatus("saving");
    try {
      const res = await postRightworkAPI(API_update_check_in_responses, {
        store_team_member_id: employeeStmId,
        check_in_uuid: checkIn.uuid,
        responses,
      });
      if (res.status === 200) {
        setSaveStatus("saved");
        // After a short delay, fade back to idle.
        setTimeout(() => {
          setSaveStatus((s) => (s === "saved" ? "idle" : s));
        }, 1500);
        return true;
      }
      // Non-200 — re-mark dirty so a future flush retries.
      flushed.forEach((uuid) => dirtyFieldUuidsRef.current.add(uuid));
      setSaveStatus("error");
      return false;
    } catch (e) {
      flushed.forEach((uuid) => dirtyFieldUuidsRef.current.add(uuid));
      setSaveStatus("error");
      return false;
    }
  }, [employeeStmId, checkIn]);

  // Schedule a debounced flush whenever responses change.
  const autosaveTimerRef = React.useRef(null);
  const scheduleAutosave = React.useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      flushResponses();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [flushResponses]);

  // Keep a ref pointer to the latest flushResponses so unmount cleanup can
  // call the current version, not a stale closure from initial mount.
  const flushResponsesRef = React.useRef(flushResponses);
  flushResponsesRef.current = flushResponses;

  // Best-effort flush on unmount: if there are pending dirty responses when
  // the component is torn down, fire the PATCH so the user's last typed
  // values aren't silently dropped.
  React.useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (dirtyFieldUuidsRef.current.size > 0) {
        flushResponsesRef.current();
      }
    };
  }, []);

  // Centralized close handler. Cancel / X / Esc / outside-click all flow
  // through here. We:
  //   1. Cancel the pending debounce timer
  //   2. Fire an immediate flush of any dirty responses
  //   3. Close the drawer immediately for snappy UX
  //   4. After the flush lands, call onSaved so the parent refetches and
  //      the (newly created or just-edited) draft appears in the history.
  // Sequencing matters: refetching before the flush completes could show
  // stale preview text on the history card. Save and Discard call onSaved
  // + onClose directly because they already coordinate their own flush.
  const handleClose = React.useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    // A brand-new check-in is created on open (API_create_check_in), so an
    // empty draft is already persisted before the manager types anything.
    // If they exit a freshly-created check-in without recording any content,
    // delete that empty draft rather than leaving a blank entry in their
    // history. Scoped to create mode so we never delete an existing check-in
    // (it may be shared / conducted) just because its fields were cleared.
    if (
      mode === "create" &&
      checkIn &&
      !checkInHasContent(checkIn, responsesByFieldUuidRef.current)
    ) {
      // Clear the dirty set so the unmount best-effort flush can't fire a
      // PATCH against the row we're about to delete.
      dirtyFieldUuidsRef.current = new Set();
      postRightworkAPI(API_delete_check_in, {
        store_team_member_id: employeeStmId,
        check_in_uuid: checkIn.uuid,
      })
        .catch(() => {})
        .then(() => {
          if (onSaved) onSaved();
        });
      onClose();
      return;
    }
    const flushPromise = dirtyFieldUuidsRef.current.size > 0
      ? flushResponses()
      : Promise.resolve(true);
    flushPromise.then(() => {
      if (onSaved) onSaved();
    });
    onClose();
  }, [flushResponses, onClose, onSaved, mode, checkIn, employeeStmId]);

  // Explicit Save button handler. Unlike handleClose (Cancel / X / Esc —
  // best-effort flush, close anyway), Save is the user's affirmative ask
  // for "make sure my edits are saved before this drawer goes away." Block
  // the close if the flush fails so the user sees the "Save failed — will
  // retry" status in-drawer and doesn't silently lose their last edits.
  const handleSave = React.useCallback(async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const ok = await (
      dirtyFieldUuidsRef.current.size > 0
        ? flushResponses()
        : Promise.resolve(true)
    );
    if (!ok) {
      // Stay open. flushResponses already flipped saveStatus to "error"
      // and re-marked the dirty set so a subsequent Save (or autosave on
      // the next edit) will retry. No onSaved → parent won't refetch
      // stale data over what the user still has in flight.
      return;
    }
    if (onSaved) onSaved();
    onClose();
  }, [flushResponses, onClose, onSaved]);

  const handleResponseChange = (fieldUuid, newValue) => {
    setResponsesByFieldUuid((prev) => ({ ...prev, [fieldUuid]: newValue }));
    dirtyFieldUuidsRef.current.add(fieldUuid);
    scheduleAutosave();
  };

  // ----- Discard / share -----

  const handleDiscard = async () => {
    if (!window.confirm("Discard this check-in? This can't be undone.")) return;
    setActionError(null);
    try {
      const res = await postRightworkAPI(API_delete_check_in, {
        store_team_member_id: employeeStmId,
        check_in_uuid: checkIn.uuid,
      });
      if (res.status === 200) {
        onSaved && onSaved();
        onClose();
      }
    } catch (e) {
      const msg = e?.response?.data?.system?.message;
      setActionError(msg || "Couldn't discard check-in.");
    }
  };

  const handleShareToggle = async () => {
    if (!checkIn) return;
    const newValue = !checkIn.is_shared_with_employee;
    // Optimistic update.
    setCheckIn((prev) => ({ ...prev, is_shared_with_employee: newValue }));
    try {
      const res = await postRightworkAPI(API_update_check_in, {
        store_team_member_id: employeeStmId,
        check_in_uuid: checkIn.uuid,
        is_shared_with_employee: newValue,
      });
      if (res.status !== 200) throw new Error();
    } catch (e) {
      // Revert on failure.
      setCheckIn((prev) => ({ ...prev, is_shared_with_employee: !newValue }));
      setActionError(e?.response?.data?.system?.message || "Couldn't update sharing.");
    }
  };

  // ----- Action item handlers -----

  const replaceActionItem = (item) => {
    setCheckIn((prev) => ({
      ...prev,
      action_items: prev.action_items.map((ai) =>
        ai.uuid === item.uuid ? item : ai,
      ),
    }));
  };

  const removeActionItem = (uuid) => {
    setCheckIn((prev) => ({
      ...prev,
      action_items: prev.action_items.filter((ai) => ai.uuid !== uuid),
    }));
  };

  const lastCommittedTextRef = React.useRef({});

  // Returns true on success, false on failure. The caller
  // (ActionItemsSection) uses this to decide whether to clear the typed
  // draft — clearing on failure would force the user to retype after a
  // transient network/backend hiccup.
  const handleActionItemAdd = async (text, dueDate) => {
    try {
      const res = await postRightworkAPI(API_create_check_in_action_item, {
        store_team_member_id: employeeStmId,
        check_in_uuid: checkIn.uuid,
        text,
        due_date: dueDate,
      });
      if (res.status === 200) {
        const newItem = res.data?.redux?.payload?.action_item;
        if (newItem) {
          lastCommittedTextRef.current[newItem.uuid] = newItem.text;
          setCheckIn((prev) => ({
            ...prev,
            action_items: [...prev.action_items, newItem],
          }));
          return true;
        }
      }
      return false;
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't add action item.");
      return false;
    }
  };

  const handleActionItemEditTextLocal = (uuid, newText) => {
    setCheckIn((prev) => ({
      ...prev,
      action_items: prev.action_items.map((ai) =>
        ai.uuid === uuid ? { ...ai, text: newText } : ai,
      ),
    }));
  };

  const handleActionItemEditTextCommit = async (uuid) => {
    const item = checkIn.action_items.find((ai) => ai.uuid === uuid);
    if (!item) return;
    if (lastCommittedTextRef.current[uuid] === item.text) return;
    if (!item.text.trim()) {
      // Treat empty as no-op; the backend rejects empty anyway.
      return;
    }
    try {
      const res = await postRightworkAPI(API_update_check_in_action_item, {
        store_team_member_id: employeeStmId,
        check_in_action_item_uuid: uuid,
        text: item.text,
      });
      if (res.status === 200) {
        const updated = res.data?.redux?.payload?.action_item;
        if (updated) {
          lastCommittedTextRef.current[uuid] = updated.text;
          replaceActionItem(updated);
        }
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't save action item text.");
    }
  };

  const handleActionItemEditDueDate = async (uuid, newDate) => {
    try {
      const res = await postRightworkAPI(API_update_check_in_action_item, {
        store_team_member_id: employeeStmId,
        check_in_action_item_uuid: uuid,
        due_date: newDate,
      });
      if (res.status === 200) {
        const updated = res.data?.redux?.payload?.action_item;
        if (updated) replaceActionItem(updated);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't save due date.");
    }
  };

  const handleActionItemToggle = async (uuid) => {
    try {
      const res = await postRightworkAPI(API_toggle_check_in_action_item, {
        store_team_member_id: employeeStmId,
        check_in_action_item_uuid: uuid,
      });
      if (res.status === 200) {
        const updated = res.data?.redux?.payload?.action_item;
        if (updated) replaceActionItem(updated);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't toggle action item.");
    }
  };

  const handleActionItemDelete = async (uuid) => {
    try {
      const res = await postRightworkAPI(API_delete_check_in_action_item, {
        store_team_member_id: employeeStmId,
        check_in_action_item_uuid: uuid,
      });
      if (res.status === 200) {
        removeActionItem(uuid);
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't delete action item.");
    }
  };

  // ----- Carry-over toggle (same toggle endpoint; refresh local list) -----

  const handleCarryOverToggle = async (uuid) => {
    try {
      const res = await postRightworkAPI(API_toggle_check_in_action_item, {
        store_team_member_id: employeeStmId,
        check_in_action_item_uuid: uuid,
      });
      if (res.status === 200) {
        const updated = res.data?.redux?.payload?.action_item;
        if (updated) {
          setPreviousOpenActionItems((prev) =>
            prev.map((it) =>
              it.uuid === uuid ? { ...it, completed_at: updated.completed_at } : it,
            ),
          );
        }
      }
    } catch (e) {
      setActionError(e?.response?.data?.system?.message || "Couldn't toggle carry-over item.");
    }
  };

  // ----- Render -----

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <div className="w-[560px] max-w-screen h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title={mode === "create" && !checkIn ? "New Check-In" : "Check-In"}
            onClose={handleClose}
          />

          {/* Status strip */}
          {checkIn && (
            <div className="px-6 pt-2 pb-4 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-slate-500">
                {saveStatus === "saving" && (
                  <span className="text-slate-500">Saving…</span>
                )}
                {saveStatus === "saved" && (
                  <span className="text-emerald-600">Saved</span>
                )}
                {saveStatus === "error" && (
                  <span className="text-rose-600">Save failed — will retry</span>
                )}
              </div>
              {/* Share toggle */}
              <label className="flex items-center gap-1 cursor-pointer">
                <span className="text-slate-700">
                  Share with employee
                </span>
                <Switch
                  size="small"
                  checked={!!checkIn.is_shared_with_employee}
                  onChange={handleShareToggle}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#6d28d9" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#6d28d9",
                    },
                  }}
                />
                {checkIn.is_shared_with_employee && (
                  <VisibilityIcon className="text-violet-700" style={{ fontSize: "14px" }} />
                )}
              </label>
            </div>
          )}

          <div className="px-6 pb-6">
            {/* Loading */}
            {isLoading && (
              <div className="flex justify-center py-12">
                <LoadSpinner />
              </div>
            )}

            {/* Load error */}
            {!isLoading && loadError && (
              <p className="text-sm text-rose-600 py-6">{loadError}</p>
            )}

            {/* Main form */}
            {!isLoading && !loadError && checkIn && (
              <div className="space-y-6">
                {/* Carry-over panel */}
                {mode === "create" && previousOpenActionItems.length > 0 && (
                  <CarryOverPanel
                    items={previousOpenActionItems}
                    onToggle={handleCarryOverToggle}
                  />
                )}

                {/* Sections */}
                {(checkIn.sections || []).map((section) => (
                  <div
                    key={section.uuid}
                    className="bg-white rounded-xl border border-slate-200 p-4"
                  >
                    <h2 className="text-base font-semibold text-slate-900 mb-1">
                      {section.order + 1}. {section.title}
                    </h2>
                    {section.prompt_help_text && (
                      <p className="text-xs text-slate-500 whitespace-pre-wrap mb-4">
                        {section.prompt_help_text}
                      </p>
                    )}
                    <div className="space-y-5">
                      {(section.fields || []).map((field) => (
                        <ConditionalFieldWrapper
                          key={field.uuid}
                          field={field}
                          responsesByFieldUuid={responsesByFieldUuid}
                          onResponseChange={handleResponseChange}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {/* Action items — key forces remount when the form switches
                    check-ins so the "add" row's draft text doesn't leak. */}
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <ActionItemsSection
                    key={checkIn.uuid}
                    actionItems={checkIn.action_items || []}
                    onAdd={handleActionItemAdd}
                    onToggle={handleActionItemToggle}
                    onDelete={handleActionItemDelete}
                    onEditTextLocal={handleActionItemEditTextLocal}
                    onEditTextCommit={handleActionItemEditTextCommit}
                    onEditDueDate={handleActionItemEditDueDate}
                  />
                </div>

                {/* Action error */}
                {actionError && (
                  <p className="text-sm text-rose-600">{actionError}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer spacer */}
          <div className="h-[140px]"></div>
        </div>

        {/* Footer: Save (primary) + Cancel (outlined) + Discard (destructive).
            Save = explicit affirmation; awaits the flush and blocks the
            close if it fails so the user sees the error and doesn't lose
            edits. Cancel / X / Esc = best-effort flush, close regardless. */}
        {checkIn && (
          <div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <button
                  onClick={handleSave}
                  disabled={saveStatus === "saving"}
                  className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 disabled:cursor-not-allowed text-white text-base font-medium rounded-xl py-3.5 transition-colors"
                >
                  {saveStatus === "saving" ? "Saving…" : "Save"}
                </button>
              </div>
              <div className="w-full mt-3 flex gap-2">
                <div className="flex-1">
                  <ButtonOutlined text="Cancel" onClick={handleClose} />
                </div>
                <button
                  onClick={handleDiscard}
                  className="text-sm font-medium text-rose-600 hover:text-rose-700 px-3"
                >
                  Discard
                </button>
              </div>
            </DrawerFooterContainerLarge>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default CheckInForm;
